import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import PageLayout from '../components/PageLayout';
import Button from '../components/Button';
import plusIcon from '../assets/plus.svg';
import { ReactComponent as UrgentIcon } from '../assets/urgent.svg';
import DeletePatientButton from '../components/DeletePatientButton';
import TodoCreateModal from '../components/TodoCreateModal';
import PdfImporter from '../components/PdfImporter';
import { socket } from '../socket';
import { mapDbPatient, mapIncomingPatient } from '../utils/patientHelpers';
import { CONFIG } from '../config/config';
import { getStep } from '../constants/statusDefinitions';
import { showTime, fromNow } from '../utils/dateHelpers';
import { getDisplayMusiqId } from '../utils/musiqId';
import { listTypesSalles } from '../api/salles';
import { useUser } from '../context/UserContext';
import { RdvPassageInline, RdvPassageModal } from '../components/RdvPassagePanel';
import toast from 'react-hot-toast';

import { Pill, ChevronDown, ScanLine, LockOpen, LockKeyholeOpen } from 'lucide-react';
import buttonsConfig from '../config/arrivee_boutons.json';

// Safelist Tailwind CSS pour s'assurer que toutes les couleurs dynamique du JSON sont bien dans le CSS
// eslint-disable-next-line no-unused-vars
const TAILWIND_SAFELIST = [
  'border-yellow-500 text-yellow-600 hover:bg-yellow-50',
  'border-blue-500 text-blue-600 hover:bg-blue-50',
  'border-orange-500 text-orange-600 hover:bg-orange-50',
  'border-red-500 text-red-600 hover:bg-red-50',
  'border-slate-400 text-slate-700 hover:bg-slate-50',
  'text-blue-700 bg-blue-50 hover:bg-blue-100 border-blue-200',
  'text-orange-700 bg-orange-50 hover:bg-orange-100 border-orange-200',
  'text-red-700 bg-red-50 hover:bg-red-100 border-red-200',
];

// =====================================================================
// Helper pour afficher les icônes dynamiquement depuis le JSON
// =====================================================================
function renderIcon(iconName, className = "") {
  if (iconName === 'Pill') return <Pill size={18} className={className} />;
  if (iconName === 'LockKeyholeOpen') return <LockKeyholeOpen size={16} className={className} />;
  if (iconName === 'LockOpen') return <LockOpen size={16} className={className} />;
  if (iconName === 'ScanLine') return <ScanLine size={16} className={className} />;
  return null;
}

// =====================================================================
// Composant pour le menu déroulant dynamique (Dropdown)
// =====================================================================
function DynamicDropdown({ btn, handleExecuteAction, hasRoleAccess }) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className="relative flex-1 min-w-0 h-full" ref={dropdownRef}>
       {/* Bouton principal */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`w-full h-full flex items-center justify-center gap-2 text-center rounded-xl border bg-white p-2 text-xs md:text-sm font-medium transition-colors ${btn.className || ''}`}
        title={btn.tooltip || btn.label}
      >
        {renderIcon(btn.icon)}
        <span className="truncate">{btn.label}</span>
        {btn.items && (
          <ChevronDown size={16} className={`shrink-0 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
        )}
      </button>

      {/* Menu déroulant */}
      {isOpen && (
        <div className="absolute top-full left-1/2 -translate-x-1/2 mt-2 w-56 sm:w-64 max-w-[calc(100vw-2rem)] bg-white border border-slate-200 rounded-xl shadow-xl z-50 overflow-hidden flex flex-col p-2 gap-2">
          {btn.items.map((item) => {
            if (item.roles && !hasRoleAccess(item.roles)) return null;
            return (
              <button
                key={item.id}
                onClick={() => {
                  setIsOpen(false);
                  handleExecuteAction(item);
                }}
                className={`w-full flex items-center justify-center gap-2 px-3 py-2 text-sm font-medium rounded-lg transition-colors border ${
                  item.className || 'text-slate-700 hover:bg-slate-50 border-slate-100'
                }`}
                title={item.tooltip || item.label}
              >
                {renderIcon(item.icon)}
                <span>{item.label}</span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

// =====================================================================
// Composant principal Arrivee
// =====================================================================
export default function Arrivee({ patients = [], setPatients }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { profile, etablissement, user: me } = useUser();
  const token = sessionStorage.getItem('token') || localStorage.getItem('token');
  const API = (CONFIG.API_URL || '/api').replace(/\/+$/, '');
  
  const trigrammeEtab = etablissement?.etablissement_trigramme || etablissement?.trigramme || 'AUT';

  const [todoOpen, setTodoOpen] = useState(false);

  const [sheetStatus, setSheetStatus] = useState({});

  const isAdmin = me?.role === 'admin' || (Array.isArray(me?.roles) && me.roles.includes('admin'));
  const isMedecin = me?.role === 'medecin' || (Array.isArray(me?.roles) && me.roles.includes('medecin'));
  const isInfirmiere = me?.role === 'infirmiere' || (Array.isArray(me?.roles) && me.roles.includes('infirmiere'));
  const isSecretaire = me?.role === 'secretaire' || (Array.isArray(me?.roles) && me.roles.includes('secretaire'));

  // 20 prénoms + 20 noms (patients tests)
  const FIRST_NAMES = [
    { fname: 'Alice', civilite: 'Mme.' },
    { fname: 'Camille', civilite: 'Mme.' },
    { fname: 'Sophie', civilite: 'Mme.' },
    { fname: 'Claire', civilite: 'Mme.' },
    { fname: 'Julie', civilite: 'Mme.' },
    { fname: 'Emma', civilite: 'Mme.' },
    { fname: 'Lucie', civilite: 'Mme.' },
    { fname: 'Manon', civilite: 'Mme.' },
    { fname: 'Laura', civilite: 'Mme.' },
    { fname: 'Chloé', civilite: 'Mme.' },
    { fname: 'Léa', civilite: 'Mme.' },
    { fname: 'Lucas', civilite: 'M.' },
    { fname: 'Thomas', civilite: 'M.' },
    { fname: 'Julien', civilite: 'M.' },
    { fname: 'Pierre', civilite: 'M.' },
    { fname: 'Antoine', civilite: 'M.' },
    { fname: 'Nicolas', civilite: 'M.' },
    { fname: 'Paul', civilite: 'M.' },
    { fname: 'Hugo', civilite: 'M.' },
    { fname: 'Maxime', civilite: 'M.' },
  ];

  const LAST_NAMES = [
    'Martin',
    'Bernard',
    'Dubois',
    'Thomas',
    'Robert',
    'Richard',
    'Petit',
    'Durand',
    'Leroy',
    'Moreau',
    'Simon',
    'Laurent',
    'Lefebvre',
    'Mercier',
    'Dupont',
    'Lambert',
    'Garcia',
    'David',
    'Bertrand',
    'Roux',
  ];

  const randomDate = (start, end) =>
    new Date(start.getTime() + Math.random() * (end.getTime() - start.getTime()));

  // ---------------------------------------------------------------------------
  // State local
  // ---------------------------------------------------------------------------
  const [selectingId, setSelectingId] = useState(null); // patient en cours d'affectation

  const userRoles = Array.isArray(me?.roles) ? me.roles : [me?.role];
  const hasRoleAccess = (allowedRoles) => {
    if (!allowedRoles || allowedRoles.length === 0) return true;
    return allowedRoles.some((r) => userRoles.includes(r));
  };

  const [successMessage, setSuccessMessage] = useState('');
  const [editRdvTime, setEditRdvTime] = useState(false);
  const [editedPassage, setEditedPassage] = useState(null);
  const [typesSalles, setTypesSalles] = useState([]);
  // setters "parking"
  const [, setImportedPatients] = useState([]); // historique d'import éventuel
  const [, setSallePreselection] = useState(''); // pré-sélection UI

  const getUid = (p) => getDisplayMusiqId(p);

  // utilitaire petit "toast" local
  const flash = (msg) => {
    setSuccessMessage(msg);
    setTimeout(() => setSuccessMessage(''), 3000);
  };

  // ---------------------------------------------------------------------------
  // WebSocket (liste + créations + updates)
  // ---------------------------------------------------------------------------
  useEffect(() => {
    const handlePatientsList = (list) => setPatients(list.map(mapDbPatient));
    const handlePatientCreated = (raw) => {
      const p = mapDbPatient(raw);
      setPatients((prev) => (prev.some((x) => x.id === p.id) ? prev : [p, ...prev]));
      if (raw?._source === 'apicrypt') {
        flash('✅ Patient importé via APICRYPT');
      }
    };
    const handlePatientDeleted = ({ id }) => {
      setPatients((prev) => prev.filter((p) => p.id !== id));
    };
    const handlePatientUpdated = (raw) => {
      const incoming = mapDbPatient(raw);
      setPatients((prev) => {
        const before = prev.find((x) => x.id === incoming.id);
        if (!before) return [incoming, ...prev];
        const merged = { ...before, ...incoming }; // <-- merge non destructif
        return prev.map((x) => (x.id === merged.id ? merged : x));
      });
    };

    socket.on('patientsList', handlePatientsList);
    socket.on('patientCreated', handlePatientCreated);
    socket.on('patientUpdated', handlePatientUpdated);
    socket.on('patientDeleted', ({ id }) => {
      setPatients((prev) => prev.filter((p) => p.id !== id));
    });
    socket.on('patientsCleared', () => setPatients([]));

    // 👇 Abonnement status-driven du calcul Sheet
    const onSheetStatus = ({ passageId, status }) => {
      if (!passageId || !status) return;
      setSheetStatus((m) => ({ ...m, [passageId]: status }));
    };

    const onSheetProgress = (payload) => {
      // compat: translate legacy started/done → pending/ready
      const s =
        payload?.stage === 'started' ? 'pending' : payload?.stage === 'done' ? 'ready' : null;
      if (payload?.passageId && s) {
        setSheetStatus((m) => ({ ...m, [payload.passageId]: s }));
      }
    };

    socket.on('sheet:status', onSheetStatus);
    socket.on('sheet:progress', onSheetProgress);

    socket.emit('getPatients');
    const ask = () => socket.emit('getPatients');
    socket.on('connect', ask);

    return () => {
      socket.off('patientsList', handlePatientsList);
      socket.off('patientCreated', handlePatientCreated);
      socket.off('patientUpdated', handlePatientUpdated);
      socket.off('patientDeleted', handlePatientDeleted);
      socket.off('sheet:status', onSheetStatus);
      socket.off('sheet:progress', onSheetProgress);
      socket.off('connect', ask);
    };
  }, [setPatients]);

  // ---------------------------------------------------------------------------
  // Pré-sélection éventuelle depuis la navigation
  // ---------------------------------------------------------------------------
  useEffect(() => {
    const sel = location.state?.sallePreselectionnee;
    if (sel) setSallePreselection(sel);
  }, [location.state, setSallePreselection]);

  const refresh = async () => {
    try {
      const data = await listTypesSalles();
      setTypesSalles(data);
    } catch (e) {
      console.log(e.message);
    }
  };

  useEffect(() => {
    refresh();
  }, []);

  // ---------------------------------------------------------------------------
  // Générer et persister 1 patient test
  // ---------------------------------------------------------------------------
  const handleGenerateTestPatients = async () => {
    const prenomRaw = FIRST_NAMES[Math.floor(Math.random() * FIRST_NAMES.length)];
    const prenom = prenomRaw.fname;
    const nomRaw = LAST_NAMES[Math.floor(Math.random() * LAST_NAMES.length)];
    const civilite = prenomRaw.civilite;
    const nom = 'DEMO_' + nomRaw.toUpperCase();
    const dob = randomDate(new Date(1940, 0, 1), new Date(2005, 11, 31));
    const dateNaissance = dob.toISOString().slice(0, 10);
    const identifiant =
      prenom[0] + nomRaw.slice(0, 3) + String(100 + Math.floor(Math.random() * 900));
    const payload = {
      prenom,
      nom,
      dateNaissance,
      identifiant,
      urgence: Math.random() < 0.3,
      civilite,
      etablissement_id: etablissement.etablissement_id,
      is_demo: true,
    };

    try {
      const res = await fetch(`${API}/passages`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const created = mapDbPatient(await res.json());
      setPatients((prev) => (prev.some((p) => p.id === created.id) ? prev : [created, ...prev]));
      flash(`✅ 1 patient test généré`);
    } catch (err) {
      console.error('❌ Création patient test :', err);
    }
  };

  // ---------------------------------------------------------------------------
  // Générer un patient Inconnu (John ou Jane DOE)
  // ---------------------------------------------------------------------------
  const handleGenerateDoe = async (sexe) => {
    const isMale = sexe === 'M';
    const prenom = isMale ? 'Patient' : 'Patiente';
    const nom = isMale ? 'NON_ENREGISTRE' : 'NON_ENREGISTREE';
    const civilite = isMale ? 'M.' : 'Mme';
    const dateNaissance = "1900-01-01"; 
    const uniqueSuffix = Date.now().toString().slice(-4) + Math.floor(Math.random() * 100);
    const identifiant = `${prenom[0]}DOE${uniqueSuffix}`;
    const payload = {
      prenom,
      nom,
      dateNaissance,
      identifiant,
      urgence: true, // Forcé en urgence
      civilite,
      etablissement_id: etablissement.etablissement_id,
      is_demo: true,
    };

    try {
      const res = await fetch(`${API}/passages`, { 
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const created = mapDbPatient(await res.json());
      setPatients((prev) => (prev.some((p) => p.id === created.id) ? prev : [created, ...prev]));
      flash(`✅ 1 patient inconnu généré : ${prenom} ${nom}`);
    } catch (err) {
      console.error(`❌ Création patient ${prenom} ${nom} :`, err);
    }
  };

  // ---------------------------------------------------------------------------
  // Vider tout (dev / préprod) — bouton demandé
  // ---------------------------------------------------------------------------
  const handlePurgeAllPatients = async () => {
    if (!window.confirm('Confirmer la suppression de TOUS les patients ?')) return;
    if (!window.confirm('Dernière alerte : action IRRÉVERSIBLE. Continuer ?')) return;
    try {
      const res = await fetch(`${API}/admin/devtools/purge-all-patients`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (!res.ok) {
        const txt = await res.text().catch(() => '');
        throw new Error(`HTTP ${res.status}${txt ? ' – ' + txt : ''}`);
      }
      setPatients([]);
      flash('🗑️ Tous les patients ont été supprimés');
    } catch (err) {
      console.error('❌ Purge patients :', err);
      alert('Échec de la suppression : ' + err.message);
    }
  };

  // ---------------------------------------------------------------------------
  // Import PDF (Doctolib → payload normalisé)
  // ---------------------------------------------------------------------------
  async function handleImported(raw) {
    const incoming = mapIncomingPatient(raw);
    if (!incoming.doctolibId || !incoming.dateNaissance) return;
    setImportedPatients((ps) => {
      const next = [...ps, incoming];
      return next.filter((p, i, arr) => arr.findIndex((q) => q.id === p.id) === i);
    });
  }

  // ---------------------------------------------------------------------------
  // Affecter une salle → PATCH /passages/:id/salle
  // ---------------------------------------------------------------------------

  async function handleAssignSalle(id, salle) {
    try {
      const res = await fetch(`${API}/passages/${encodeURIComponent(id)}/salle`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ salle: salle.salle_id }),
      });
      if (!res.ok) {
        const txt = await res.text().catch(() => '');
        throw new Error(`HTTP ${res.status}${txt ? ' – ' + txt : ''}`);
      }
      const p = mapDbPatient(await res.json());
      setPatients((prev) => prev.map((x) => (x.id === p.id ? p : x)));
      setSelectingId(null);
      setSallePreselection('');
      flash(`✅ ${p.prenom} affecté·e à la salle ${salle.salle_nom}`);
    } catch (err) {
      console.error('assignSalle error :', err);
      alert('❌ Impossible d’affecter en salle : ' + err.message);
    }
  }

  async function handleRDVChange(id, value) {
    try {
      const res = await fetch(`${API}/passages/${encodeURIComponent(id)}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ date_rdv: value || null }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const p = mapDbPatient(await res.json());
      setPatients((prev) => prev.map((x) => (x.id === p.id ? p : x)));
    } catch (err) {
      console.error('rdv update error :', err);
      alert('Impossible de modifier le Rendez-vous : ' + err.message);
    }
  }

  async function handleTypeSalleChange(id, value) {
    try {
      const res = await fetch(`${API}/passages/${encodeURIComponent(id)}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ type_salle: value || null }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const p = mapDbPatient(await res.json());
      setPatients((prev) => prev.map((x) => (x.id === p.id ? p : x)));
    } catch (err) {
      console.error('type de salle update error :', err);
      alert('Impossible de modifier le type de salle : ' + err.message);
    }
  }

  async function handleDeletePatient(p) {
    if (!p?.id) return;
    try {
      const r = await fetch(`${API}/passages/${p.id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      setPatients((prev) => prev.filter((x) => x.id !== p.id));
      if (selectingId === p.id) setSelectingId(null);
      flash('🗑️ Patient supprimé');
      toast.success('🗑️ Patient supprimé');
    } catch (e) {
      console.error('delete patient', e);
      alert('Suppression impossible');
    }
  }

  // ---------------------------------------------------------------------------
  // Toggle urgence (WS)
  // ---------------------------------------------------------------------------
  function handleUrgenceToggle(id) {
    const old = patients.find((p) => p.id === id);
    if (!old) return;
    const updated = { ...old, urgence: !old.urgence };
    setPatients((prev) => prev.map((p) => (p.id === id ? updated : p)));
    socket.emit('toggleUrgence', { id, urgence: updated.urgence }, (err) => {
      if (err) setPatients((prev) => prev.map((p) => (p.id === id ? old : p)));
    });
  }

  // ---------------------------------------------------------------------------
  // Listes / Dérivés UI
  // - nonAssignes : pas de salle & step < 7
  // - tri : urgence prioritaire, puis plus ancien en premier (heure_entree || created_at)
  // ---------------------------------------------------------------------------
  const arrivalOf = (p) => p.heureEntree || p.heure_entree || p.createdAt || p.created_at || null;
  const sortCriteriaOf = (p) => p.date_rdv || arrivalOf(p);

  const nonAssignes = useMemo(() => {
    const base = patients.filter(
      (p) => !p.salle_id && Number(getStep(p)) < 7 && p.etablissement_id === etablissement.etablissement_id
    );
    return base.slice().sort((a, b) => {
      if (a.urgence !== b.urgence) return a.urgence ? -1 : 1;
      const ta = new Date(sortCriteriaOf(a) || 0).getTime();
      const tb = new Date(sortCriteriaOf(b) || 0).getTime();
      return ta - tb;
    });
  }, [patients, etablissement]);

  const salleOccupee = useMemo(() => {
    const map = new Map();
    patients
      .filter((p) => p.salle_id && Number(getStep(p)) < 7)
      .forEach((p) => {
        if (!map.has(p.salle_id)) map.set(p.salle_id, []);
        map.get(p.salle_id).push(p);
      });
    return map;
  }, [patients]);

  const actionHandlers = { 
    open_todo: () => setTodoOpen(true),
    generate_test_patient: handleGenerateTestPatients,
    generate_doe: (params) => handleGenerateDoe(params?.sexe || 'M'),
    purge_all_patients: handlePurgeAllPatients,
    open_scanner: () => navigate('/scanner'),

    // =================================================================
    // LOGIQUE HOME ASSISTANT
    // =================================================================
    trigger_ha_armoire: async (_params, btn) => {
      try {
        const res = await fetch(`${API}/armoire/open`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            entity_id: btn.id,
            path: btn.path,
          }),
        });

        if (!res.ok) {
          const data = await res.json().catch(() => ({}));
          throw new Error(data.error);
        } 
        toast.success(`Signal envoyé : ${btn.label}`);
      } catch (err) {
        console.error("Erreur trigger HA :", err);
        toast.error(`Erreur d'ouverture : ${err.message}`);
      }
    },
  };
  const handleExecuteAction = (btn) => { const handler = actionHandlers[btn.action]; if (handler) handler(btn.params, btn); };


  // commenter que le nécessaire pour masquer le bouton "Scanner" ou non
  //const buttonsToDisplay = useMemo(() => [...(buttonsConfig[trigrammeEtab] || []), ...(buttonsConfig.Commun || [])], [trigrammeEtab]);
  const buttonsToDisplay = useMemo(() => {
    const allButtons = [...(buttonsConfig[trigrammeEtab] || []), ...(buttonsConfig.Commun || [])];
    
    // On filtre pour masquer le bouton "Scanner" (qu'il soit un bouton principal ou dans un menu déroulant)
    return allButtons
      .map(btn => {
        if (btn.items) {
          return { ...btn, items: btn.items.filter(item => item.action !== 'open_scanner') };
        }
        return btn;
      })
      .filter(btn => btn.action !== 'open_scanner' && (!btn.items || btn.items.length > 0));
  }, [trigrammeEtab]);

   // Groupement par ligne Ordinateur (rowDesktop)
  const buttonRowsDesktop = useMemo(() => {
    const visible = buttonsToDisplay.filter((btn) => hasRoleAccess(btn.roles));
    const grouped = {};
    visible.forEach((btn) => { const r = btn.rowDesktop || 1; if (!grouped[r]) grouped[r] = []; grouped[r].push(btn); });
    return Object.keys(grouped).sort((a, b) => Number(a) - Number(b)).map((r) => grouped[r]);
  }, [buttonsToDisplay, userRoles]);

  // Groupement par ligne Mobile (rowMobile)
  const buttonRowsMobile = useMemo(() => {
    const visible = buttonsToDisplay.filter((btn) => hasRoleAccess(btn.roles));
    const grouped = {};
    visible.forEach((btn) => { const r = btn.rowMobile || 1; if (!grouped[r]) grouped[r] = []; grouped[r].push(btn); });
    return Object.keys(grouped).sort((a, b) => Number(a) - Number(b)).map((r) => grouped[r]);
  }, [buttonsToDisplay, userRoles]);

  // Rendu générique d'un bouton
  const renderSingleButton = (btn) => {
    if (btn.type === 'dropdown') {
      return (
        <DynamicDropdown 
          key={btn.id} 
          btn={btn} 
          handleExecuteAction={handleExecuteAction} 
          hasRoleAccess={hasRoleAccess} 
        />
      );
    }
    return (
      <button
        key={btn.id}
        onClick={() => handleExecuteAction(btn)}
        className={`flex-1 min-w-0 flex items-center justify-center gap-2 text-center rounded-xl border bg-white p-2 text-xs md:text-sm font-medium transition-colors ${btn.className || ''}`}
        title={btn.tooltip || btn.label}
      >
        {renderIcon(btn.icon)}
        <span className="truncate">{btn.label}</span>
      </button>
    );
  };

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------
  return (
    <PageLayout title="Liste arrivée">
      {/* Bandeau d'actions */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 items-start mb-4">
        {/* Import PDF */}
        <div className="lg:col-span-2">
          <PdfImporter onPatientImported={handleImported} />
        </div>

        {/* Actions dynamiques */}
        <div className="w-full">
          {/* Disposition Mobile (< md) */}
          <div className="flex flex-col gap-2 w-full md:hidden">
            {buttonRowsMobile.map((rowButtons, rowIndex) => (
              <div key={rowIndex} className="flex gap-2 w-full min-h-[42px]">
                {rowButtons.map((btn) => renderSingleButton(btn))}
              </div>
            ))}
          </div>
          {/* Disposition Ordinateur (>= md) */}
          <div className="hidden md:flex flex-col gap-2 w-full">
            {buttonRowsDesktop.map((rowButtons, rowIndex) => (
              <div key={rowIndex} className="flex gap-2 w-full min-h-[42px]">
                {rowButtons.map((btn) => renderSingleButton(btn))}
              </div>
            ))}
          </div>
        </div>
      </div>

      {successMessage && <p className="text-center text-green-600 font-medium">{successMessage}</p>}
      <RdvPassageModal
        passage={editedPassage}
        onConfirm={(id, datetime) => handleRDVChange(id, datetime)}
        onClose={() => {
          setEditedPassage(null);
          setEditRdvTime(false);
        }}
      />
      
      <main className="flex-grow overflow-auto px-4 py-2 space-y-4">
        {nonAssignes.length === 0 ? (
          <p className="text-center text-gray-400">Aucun patient en attente</p>
        ) : (
          nonAssignes.map((p) => {
            const ddn = p.dateNaissance
              ? new Date(p.dateNaissance).toLocaleDateString('fr-FR')
              : '-';
            const arrival = arrivalOf(p);
            const waited = arrival ? fromNow(arrival) : '—';
            const uid = getUid(p);

            return (
              <div key={p.id} className="space-y-2">
                <div
                  className={`relative flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 border pb-4 px-4 pt-4 rounded ${
                    p.urgence ? 'border-red-500 bg-red-50' : 'border-gray-200'
                  }`}
                >
                  {p.urgence && (
                    <div className="absolute top-0 left-0 h-full w-2 bg-[#B3000A] rounded-l" />
                  )}

                  {/* Informations patient */}
                  <div className="flex items-start gap-4">
                   <button
                      onClick={() => handleUrgenceToggle(p.id)}
                      className="p-1 mt-1"
                      aria-label="Basculer le mode urgence"
                    >
                      <UrgentIcon
                        className={`h-12 w-12 transition-all duration-200 ${
                          p.urgence ? 'text-[#B3000A] opacity-100' : 'text-[#B3000A] opacity-20'
                        }`}
                      />
                    </button>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap text-xl font-semibold text-[#B3000A]">
                         <span>
                        {p.civilite} <span className="uppercase">{p.nom}</span> {p.prenom}
                      </span>
                      {p.urgence && (
                        <span className="inline-flex items-center rounded-full bg-[#B3000A] px-2 py-0.5 text-[10px] font-bold text-white shrink-0">
                          URGENCE
                        </span>
                      )}
                    </div>

                      {/* Infos patient — 1 ligne par info (responsive) */}
                      <div className="mt-1 space-y-1 text-sm text-gray-700">
                        <div>
                          Date de naissance : <span className="font-medium">{ddn}</span>
                        </div>

                        {uid && (
                          <div className="font-mono text-xs text-gray-500 break-all">
                            MusiQ&nbsp;: {uid}
                          </div>
                        )}

                        {/* Heure d'arrivée + attente */}
                        <div>
                          Arrivée&nbsp;: <span className="font-medium">{showTime(arrival)}</span>
                          {' · '}
                          Attente&nbsp;: <span className="font-medium">{waited}</span>
                        </div>

                        <div className="flex items-center gap-2 pt-1">
                          <span className="text-xs text-gray-500">Type de salle :</span>
                          <select
                            className="text-sm border rounded px-2 py-1"
                            value={p.type_salle || ''}
                            onChange={(e) => {
                              handleTypeSalleChange(p.id, e.target.value || null);
                            }}
                          >
                            <option value="">—</option>
                            {typesSalles.map((ts) => (
                              <option key={ts.id} value={ts.id}>
                                {ts.libelle}
                              </option>
                            ))}
                          </select>
                        </div>

                        <div className="flex items-center gap-2 pt-1">
                          <span className="font-semibold text-slate-600">RDV : </span>
                          <RdvPassageInline
                           p={p}
                            onEdit={(passage) => {
                              setEditedPassage(passage);
                              setEditRdvTime(true);
                            }}
                            onClear={(id) => handleRDVChange(id, null)}
                          />
                        </div>
                      </div>
                    </div>
                     {(isAdmin || isMedecin) && (
                      <div className="self-start -mr-1">
                        <DeletePatientButton
                          patient={p}
                          token={token}
                          onDeleted={() => handleDeletePatient(p)}
                          size={20}
                        />
                      </div>
                    )}
                  </div>
                   {/* Bouton “affecter” */}
                  <Button
                    onClick={() => setSelectingId((cur) => (cur === p.id ? null : p.id))}
                    className="h-14 w-14 flex items-center justify-center border-2 border-red-300 border-dashed rounded-lg hover:border-red-500 transition p-0"
                    aria-label={`Ajouter ${p.prenom}`}
                  >
                    <img
                      src={plusIcon}
                      alt="+"
                      className="h-8 w-8"
                      style={{
                        filter:
                          'invert(29%) sepia(86%) saturate(611%) hue-rotate(339deg) brightness(95%) contrast(94%)',
                      }}
                    />
                  </Button>
                </div>

                {/* Choix de la salle */}
                {selectingId === p.id && (
                  <div className="px-4 pb-4">
                    <div className="border-2 border-dashed border-red-300 rounded-lg bg-white p-3 space-y-2">
                      <p className="text-sm text-red-600 font-medium">
                        Choisir une salle pour {p.prenom} {p.nom}
                      </p>

                      {/* grille auto-fit responsive */}
                      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-2">
                        {etablissement?.salles
                          ?.slice()
                          .sort((a, b) => a.salle_nom.localeCompare(b.salle_nom))
                          .map((salle) => {
                            const occup = salleOccupee.get(salle.salle_id);
                            return (
                              <button
                                key={salle.salle_id}
                                onClick={() => handleAssignSalle(p.id, salle)}
                                className="py-1 text-sm border rounded px-2 text-center transition text-red-600 border-red-300 hover:bg-red-50"
                              >
                                <span className="font-medium">Salle {salle.salle_nom}</span>
                                {occup && (
                                  <span className="block text-[11px] text-gray-500 font-normal leading-snug">
                                    {occup.map((o) => `• ${o.prenom} ${o.nom}`).join('  ')}
                                  </span>
                                )}
                              </button>
                            );
                          })}
                      </div>

                      <button
                        onClick={() => setSelectingId(null)}
                        className="mt-2 block text-xs text-gray-500 underline"
                      >
                        Annuler
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })
        )}
      </main>

      <TodoCreateModal
        open={todoOpen}
        onClose={() => setTodoOpen(false)}
        profilDemandeurId={profile?.profile_id}
        onCreated={() => {
          setSuccessMessage('✅ To Do créée');
          setTimeout(() => setSuccessMessage(''), 2500);
        }}
      />

    </PageLayout>
  );
}